import React from 'react';
import { UserProfile, AppView } from '../../types';
import { CloudOff } from 'lucide-react';
import PlanUpgradeModal from '../../modules/erp/components/PlanUpgradeModal';
import PaywallModal, { PaywallInitialPlan } from '../PaywallModal';
import TrialBanner from '../TrialBanner';
import GracePeriodBanner from '../GracePeriodBanner';
import TeamManagementModal from '../../modules/erp/components/TeamManagementModal';
import SpecialistBottomNav, { SpecialistTab } from '../../modules/erp/components/SpecialistBottomNav';
import SpecialistQuickActionSheet from '../../modules/erp/components/SpecialistQuickActionSheet';
import { ActiveView } from '../../modules/erp/hooks/useErpPlaygroundState';

export interface GlobalModalsContainerProps {
  view: AppView;
  user: UserProfile | null;
  isOffline: boolean;
  isActivatingPro: boolean;
  isActivatingTrial: boolean;
  upgradeModalOpen: boolean;
  setUpgradeModalOpen: (open: boolean) => void;
  upgradeRequiredTier: 'pro' | 'premium' | 'enterprise';
  upgradeFeatureName: string;
  upgradeFeatureDesc?: string;
  paywallModalOpen: boolean;
  setPaywallModalOpen: (open: boolean) => void;
  paywallInitialPlan: PaywallInitialPlan;
  setPaywallInitialPlan: (plan: PaywallInitialPlan) => void;
  teamModalOpen: boolean;
  setTeamModalOpen: (open: boolean) => void;
  onStartTrial: () => void;
  isSpecialistPage?: boolean;
  shouldShowBottomNav?: boolean;
  isKeyboardOpen?: boolean;
  isEnterpriseUser?: boolean;
  isSpecialistUser?: boolean;
  specialistActiveTab: SpecialistTab | null;
  onSelectSpecialistBottomTab: (tab: SpecialistTab) => void;
  specialistQuickActionOpen: boolean;
  setSpecialistQuickActionOpen: (open: boolean) => void;
  onSpecialistActionSheetNavigate: (target: ActiveView) => void;
}

export const GlobalModalsContainer: React.FC<GlobalModalsContainerProps> = ({
  view,
  user,
  isOffline,
  isActivatingPro,
  isActivatingTrial,
  upgradeModalOpen,
  setUpgradeModalOpen,
  upgradeRequiredTier,
  upgradeFeatureName,
  upgradeFeatureDesc,
  paywallModalOpen,
  setPaywallModalOpen,
  paywallInitialPlan,
  setPaywallInitialPlan,
  teamModalOpen,
  setTeamModalOpen,
  onStartTrial,
  isSpecialistPage,
  shouldShowBottomNav,
  isKeyboardOpen,
  isEnterpriseUser,
  isSpecialistUser,
  specialistActiveTab,
  onSelectSpecialistBottomTab,
  specialistQuickActionOpen,
  setSpecialistQuickActionOpen,
  onSpecialistActionSheetNavigate,
}) => {
  return (
    <>
      {view !== 'speed_pos' && (
        <>
          <TrialBanner
            user={user}
            onStartTrial={onStartTrial}
            onOpenPaywall={() => {
              setPaywallInitialPlan('specialist');
              setPaywallModalOpen(true);
            }}
            isActivatingTrial={isActivatingTrial}
          />
          <GracePeriodBanner
            user={user}
            onOpenPaywall={() => {
              setPaywallInitialPlan(
                (user?.plan === 'Empresa' || user?.plan === 'Empresarial' || user?.plan === 'Enterprise')
                  ? 'enterprise_anual_pix'
                  : user?.plan === 'Pro'
                    ? 'monthly'
                    : 'specialist_anual_pix'
              );
              setPaywallModalOpen(true);
            }}
          />
        </>
      )}

      {isOffline && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-100 bg-amber-500 text-zinc-950 px-4 py-2.5 rounded-full flex items-center gap-2 text-xs font-black shadow-2xl border border-amber-400 animate-bounce">
          <CloudOff className="h-4 w-4 animate-pulse" />
          <span>Você está offline. Alterações serão salvas localmente.</span>
        </div>
      )}

      {isActivatingPro && (
        <div className="fixed inset-0 bg-neutral-950/85 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center" style={{ zIndex: 9999 }}>
          <div className="w-16 h-16 border-4 border-brand border-t-transparent rounded-full animate-spin mb-6" />
          <h2 className="text-xl font-bold text-white mb-2">Ativando plano Pro...</h2>
          <p className="text-sm text-neutral-400 max-w-xs">
            Confirmando o seu pagamento com o Mercado Pago. Isso não deve demorar mais do que alguns segundos.
          </p>
        </div>
      )}

      <PlanUpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        requiredTier={upgradeRequiredTier}
        currentTier={(user?.plan === 'Empresa' || user?.plan === 'Empresarial' || user?.plan === 'Enterprise') ? 'enterprise' : (user?.plan === 'Especialista' || user?.isTrialActive) ? 'premium' : user?.plan === 'Pro' ? 'pro' : 'free'}
        featureName={upgradeFeatureName}
        featureDescription={upgradeFeatureDesc}
        trialUsed={user?.trialUsed}
        onStartTrial={onStartTrial}
        isActivatingTrial={isActivatingTrial}
        onSelectPlan={(tier) => {
          setUpgradeModalOpen(false);
          setPaywallInitialPlan(tier === 'enterprise' ? 'enterprise_anual_pix' : tier === 'premium' ? 'specialist' : 'annual');
          setPaywallModalOpen(true);
        }}
      />

      <PaywallModal
        isOpen={paywallModalOpen}
        initialPlan={paywallInitialPlan}
        onClose={() => setPaywallModalOpen(false)}
      />

      <TeamManagementModal
        isOpen={teamModalOpen}
        onClose={() => setTeamModalOpen(false)}
        businessName={user?.businessName || user?.ownerName}
      />

      {(shouldShowBottomNav ?? isSpecialistPage) && (
        <>
          <SpecialistBottomNav
            activeTab={specialistActiveTab}
            onSelectTab={onSelectSpecialistBottomTab}
            onOpenQuickActions={() => setSpecialistQuickActionOpen(true)}
            plan={user?.plan}
            isMasterUser={user?.isMasterUser}
            isEnterpriseUser={isEnterpriseUser}
            isSpecialistUser={isSpecialistUser}
            isKeyboardOpen={isKeyboardOpen}
          />

          <SpecialistQuickActionSheet
            isOpen={specialistQuickActionOpen}
            onClose={() => setSpecialistQuickActionOpen(false)}
            onNavigate={onSpecialistActionSheetNavigate}
            plan={user?.plan}
            isMasterUser={user?.isMasterUser}
          />
        </>
      )}
    </>
  );
};
